import '@xyflow/react/dist/style.css';
import { useState, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  useReactFlow,
  type NodeTypes,
  type EdgeTypes,
  type DefaultEdgeOptions,
} from '@xyflow/react';
import { TreeChartContext } from './TreeChartContext';
import { TreeEdge } from './TreeEdge';
import { computeTreeLayout } from './computeTreeLayout';
import type { TreeNodeDef, TreeLayoutConfig } from './types';

// Defined outside component so ReactFlow doesn't re-register on every render
const TREE_EDGE_TYPES: EdgeTypes = {
  'tree-default': TreeEdge,
};

const DEFAULT_EDGE_OPTIONS: DefaultEdgeOptions = {
  type: 'tree-default',
  style: { stroke: 'var(--color-muted-foreground)', strokeWidth: 1.5, opacity: 0.6 },
};

interface TreeChartProps {
  nodes: TreeNodeDef[];
  nodeTypes: NodeTypes;
  layoutConfig?: Partial<TreeLayoutConfig>;
  defaultEdgeOptions?: DefaultEdgeOptions;
  className?: string;
}

// Must be rendered inside ReactFlowProvider
export function TreeChart({
  nodes: nodeDefs,
  nodeTypes,
  layoutConfig,
  defaultEdgeOptions,
  className,
}: TreeChartProps) {
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const { fitView } = useReactFlow();

  const config: TreeLayoutConfig = {
    hGap: layoutConfig?.hGap ?? 40,
    vGap: layoutConfig?.vGap ?? 60,
  };

  const toggleCollapse = (id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { nodes, edges } = computeTreeLayout(nodeDefs, collapsedIds, config);

  const chartKey = [
    nodeDefs
      .map((n) => n.id)
      .sort()
      .join(','),
    [...collapsedIds].sort().join(','),
  ].join('|');

  useEffect(() => {
    if (nodes.length > 0) {
      const timer = setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 150);
      return () => clearTimeout(timer);
    }
  }, [chartKey, nodes.length, fitView]);

  return (
    <TreeChartContext.Provider value={{ collapsedIds, toggleCollapse }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={TREE_EDGE_TYPES}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnScroll={false}
        zoomOnScroll={true}
        panOnDrag={true}
        minZoom={0.1}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={defaultEdgeOptions ?? DEFAULT_EDGE_OPTIONS}
        className={className}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="var(--color-border)"
        />
      </ReactFlow>
    </TreeChartContext.Provider>
  );
}
